import { CustomPageType } from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useApiClientContext } from '../context';

const BASE_QUERY_KEY = 'SurveyCustomPageStyle';

interface CustomPageStyleResponse {
	html?: string;
	hasCustomization: boolean;
	error?: string;
}

/**
 * Fetches custom page styles for a survey by token with fallback chain support.
 *
 * Fallback Chain (implemented by backend):
 * 1. Survey-specific customization (if brandingOverride exists)
 * 2. Account-level customization
 * 3. System default (no customization)
 *
 * Backend API Requirements:
 * - Endpoint: GET /survey/byToken/custom-page-style/{type}?token={token}
 * - Backend must handle fallback logic automatically
 * - Returns 404 if no customization exists at any level
 * - Returns HTML/CSS content if customization found
 *
 * Query Key Structure:
 * - [BASE_QUERY_KEY, token, type]
 * - Each survey token gets its own cache entry
 * - Type differentiation allows separate caching for Header vs Footer
 *
 * @param token - Survey token for identifying the specific survey
 * @param type - Type of customization (Header, Footer, etc.)
 * @param enabled - Whether the query should execute
 */
export const useCustomPageStyleByToken = ({
	token,
	type,
	enabled = true,
}: {
	token?: string;
	type: CustomPageType;
	enabled?: boolean;
}) => {
	const { apiClient } = useApiClientContext();

	return useQuery<CustomPageStyleResponse>({
		enabled: enabled && !!token,
		queryFn: async (): Promise<CustomPageStyleResponse> => {
			try {
				// Backend handles fallback chain:
				// 1. Check survey-specific override
				// 2. Fall back to account-level customization
				// 3. Fall back to no customization
				const response = await fetch(`${apiClient.baseUrl}/survey/byToken/custom-page-style/${type}?token=${token}`);

				if (!response.ok) {
					if (response.status === 404) {
						return { hasCustomization: false };
					}
					return { hasCustomization: false, error: `HTTP ${response.status}: ${response.statusText}` };
				}

				const htmlContent = await response.text();

				if (!htmlContent || htmlContent.trim() === '') {
					return { hasCustomization: false, error: 'Empty response received' };
				}

				return {
					hasCustomization: true,
					html: htmlContent,
				};
			} catch (networkError) {
				console.error('Network error fetching custom page style:', networkError);
				return { hasCustomization: false, error: 'Network error occurred' };
			}
		},
		// Query key includes token and type to ensure proper caching per survey and style type
		queryKey: [BASE_QUERY_KEY, token, type],
		retry: (failureCount, error) => {
			// Only retry on network errors, not on 4xx client errors
			return failureCount < 2 && !(error as any)?.message?.includes('HTTP 4');
		},
	});
};

export const useCustomPageStyleByAgentTemplate = ({
	agentTemplateId,
	clientIdentifier = 'web-client',
	type,
	enabled = true,
}: {
	agentTemplateId?: string;
	clientIdentifier?: string;
	type: CustomPageType;
	enabled?: boolean;
}) => {
	const { apiClient } = useApiClientContext();

	return useQuery<CustomPageStyleResponse>({
		enabled: enabled && !!agentTemplateId,
		queryFn: async (): Promise<CustomPageStyleResponse> => {
			try {
				const response = await fetch(
					`${apiClient.baseUrl}/public/aiConversation/byAgentTemplate/custom-page-style/${type}`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							AgentTemplateId: agentTemplateId,
							ClientIdentifier: clientIdentifier,
						}),
					}
				);

				if (!response.ok) {
					if (response.status === 404) {
						return { hasCustomization: false };
					}
					return { hasCustomization: false, error: `HTTP ${response.status}: ${response.statusText}` };
				}

				const htmlContent = await response.text();

				if (!htmlContent || htmlContent.trim() === '') {
					return { hasCustomization: false, error: 'Empty response received' };
				}

				return {
					hasCustomization: true,
					html: htmlContent,
				};
			} catch (networkError) {
				console.error('Network error fetching custom page style:', networkError);
				return { hasCustomization: false, error: 'Network error occurred' };
			}
		},
		// Query key includes agentTemplateId and type to ensure proper caching per agent template and style type
		queryKey: [BASE_QUERY_KEY, agentTemplateId, type],
		retry: (failureCount, error) => {
			// Only retry on network errors, not on 4xx client errors
			return failureCount < 2 && !(error as any)?.message?.includes('HTTP 4');
		},
	});
};
